import React, { useEffect, useState } from "react";
import api, { API_BASE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Download, FileText, FileSpreadsheet, Layers, MessageCircle } from "lucide-react";

const REPORTS = [
  { key: "employee_wise",   label: "Employee-wise Task Report" },
  { key: "client_wise",     label: "Client-wise Task Report" },
  { key: "pending",         label: "Pending Work Report" },
  { key: "completed",       label: "Completed Work Report" },
  { key: "overdue",         label: "Overdue Report" },
  { key: "payment_pending", label: "Payment Pending Report" },
  { key: "documents_pending", label: "Documents Pending Report" },
  { key: "new_leads",       label: "New Leads Report" },
  { key: "follow_up",       label: "Follow-up Report" },
  { key: "performance",     label: "Performance Report" },
];

export default function Reports() {
  const [active, setActive] = useState("employee_wise");
  const [data, setData] = useState(null);

  useEffect(() => {
    setData(null);
    api.get(`/reports/${active}`).then(r => setData(r.data));
  }, [active]);

  const exportUrl = (format) => `${API_BASE}/reports/${active}/export?format=${format}`;

  const download = async (format, path) => {
    const res = await api.get(path || `/reports/${active}/export?format=${format}`, { responseType: "blob" });
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement("a");
    a.href = url;
    a.download = path ? `daily-reports.xlsx` : `${active}.${format}`;
    a.click();
    window.URL.revokeObjectURL(url);
  };
  const shareWhatsApp = () => {
    if (!data) return;
    const top = data.rows.slice(0, 8).map((r, i) => `${i + 1}. ${data.columns.slice(0, 3).map(c => r[c]).filter(Boolean).join(" · ")}`).join("\n");
    const text = `*${data.title}*\n${new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })} · ${data.rows.length} record(s)\n\n${top}${data.rows.length > 8 ? `\n…and ${data.rows.length - 8} more` : ""}\n\nFull report: ${window.location.origin}/reports`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
  };

  return (
    <div className="grid grid-cols-1 xl:grid-cols-[280px_1fr] gap-6">
      <aside className="bg-white border border-zinc-200">
        <div className="px-4 py-3 border-b border-zinc-200 text-[10px] tracking-[0.16em] uppercase font-semibold text-zinc-500">Daily Reports</div>
        <ul>
          {REPORTS.map(r => (
            <li key={r.key}>
              <button
                data-testid={`report-tab-${r.key}`}
                onClick={() => setActive(r.key)}
                className={`w-full text-left px-4 py-2.5 text-sm border-l-2 transition-colors ${
                  active === r.key
                    ? "border-zinc-900 bg-zinc-50 font-semibold"
                    : "border-transparent hover:bg-zinc-50 text-zinc-700"
                }`}
              >
                {r.label}
              </button>
            </li>
          ))}
        </ul>
      </aside>

      <section className="bg-white border border-zinc-200 p-6">
        <header className="flex flex-wrap items-end justify-between gap-3 mb-4">
          <div>
            <div className="text-[10px] tracking-[0.16em] uppercase font-semibold text-zinc-500">Report</div>
            <h2 className="font-heading font-bold tracking-tight text-2xl">{data?.title || "Loading…"}</h2>
          </div>
          <div className="flex flex-wrap gap-2 justify-end">
            <Button data-testid="export-csv-btn" onClick={() => download("csv")} variant="outline" className="rounded-none border-zinc-300">
              <Download size={14} className="mr-1.5" /> CSV
            </Button>
            <Button data-testid="export-xlsx-btn" onClick={() => download("xlsx")} variant="outline" className="rounded-none border-zinc-300">
              <FileSpreadsheet size={14} className="mr-1.5" /> Excel
            </Button>
            <Button data-testid="export-pdf-btn" onClick={() => download("pdf")} className="rounded-none bg-zinc-900 hover:bg-zinc-800 text-white">
              <FileText size={14} className="mr-1.5" /> PDF
            </Button>
            <Button data-testid="export-all-btn" onClick={() => download("xlsx", "/reports-bundle.xlsx")} variant="outline" className="rounded-none border-zinc-300" title="All 10 reports as one Excel workbook">
              <Layers size={14} className="mr-1.5" /> All 10 (Excel)
            </Button>
            <Button data-testid="share-whatsapp-btn" onClick={shareWhatsApp} variant="outline" className="rounded-none border-emerald-300 text-emerald-700 hover:bg-emerald-50">
              <MessageCircle size={14} className="mr-1.5" /> WhatsApp
            </Button>
          </div>
        </header>

        {!data ? (
          <div className="text-sm text-zinc-500 py-16 text-center">Fetching…</div>
        ) : data.rows.length === 0 ? (
          <div className="text-sm text-zinc-500 py-16 text-center">No records for this report.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50">
                <tr className="text-[10px] tracking-[0.14em] uppercase text-zinc-500 border-b border-zinc-200">
                  {data.columns.map(c => (
                    <th key={c} className="text-left font-semibold px-3 py-2.5 whitespace-nowrap">{c}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="stripe-rows">
                {data.rows.map((r, i) => (
                  <tr key={i} data-testid={`report-row-${i}`} className="border-t border-zinc-100">
                    {data.columns.map(c => (
                      <td key={c} className="px-3 py-2.5 whitespace-nowrap">{String(r[c] ?? "")}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="mt-4 text-xs text-zinc-500 tabular">{data.rows.length} row(s)</div>
          </div>
        )}
      </section>
    </div>
  );
}
